// Login — центрированная карточка 480px на Whisper White фоне.
// Sprint 11: реальный auth-flow через useAuth().signIn (POST /auth/login).
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { useTenant } from '@/lib/tenant-context';

export function LoginPage(): JSX.Element {
  const { tenantSlug, setTenant } = useTenant();
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      const outcome = await signIn(email, password);
      if (outcome.kind === 'ok') {
        // Прокидываем tenant_id в контекст — query keys и брендирование.
        setTenant(outcome.user.tenantId);
        // Стартовый экран решает HomeRedirect (по правам): дашборд или расписание.
        navigate('/', { replace: true });
        return;
      }
      if (outcome.kind === 'mfa_setup_required') {
        setInfo(
          'Для роли «Босс» требуется первичная настройка двухфакторной аутентификации (2FA). Обратитесь к процедуре owner-recovery для привязки TOTP.',
        );
        return;
      }
      // mfa_required
      setInfo(
        'Для этого аккаунта включена двухфакторная аутентификация. Введите код из приложения-аутентификатора на следующем шаге.',
      );
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          err.status === 401
            ? 'Неверный email или пароль.'
            : (err.detail ?? 'Не удалось войти. Попробуйте ещё раз.'),
        );
      } else {
        setError('Сетевая ошибка. Проверьте соединение и попробуйте снова.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-whisper-white p-4">
      <Card className="w-full max-w-[480px] p-10">
        <CardHeader className="pb-6 text-center">
          <CardTitle className="text-heading-sm font-medium tracking-tight">
            Dance School CRM
          </CardTitle>
          <CardDescription className="pt-1 text-sm text-muted-foreground">
            {tenantSlug ? `${tenantSlug}.app.ru` : 'Введите данные для входа'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit} noValidate>
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium text-ink-black">
                Email
              </label>
              <Input
                id="email"
                type="email"
                placeholder="admin@school.ru"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium text-ink-black">
                Пароль
              </label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                required
              />
            </div>

            {error ? (
              <p
                role="alert"
                className="rounded-base bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {error}
              </p>
            ) : null}
            {info ? (
              <p
                role="status"
                className="rounded-base bg-lavender-mist px-3 py-2 text-sm text-ink-black"
              >
                {info}
              </p>
            ) : null}

            <Button type="submit" size="lg" className="w-full" disabled={submitting}>
              {submitting ? 'Вход…' : 'Войти'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
